import React, { useEffect, useState } from 'react';
import {
  useReactFlow,
  // useStoreApi,
  getConnectedEdges,
  Controls,
  MiniMap,
} from 'reactflow';
import { toast } from 'react-toastify';
import { v4 as uuidv4 } from 'uuid';
import { noop } from 'lodash';

import HelpCircle from 'lucide-react/dist/esm/icons/help-circle';
import { Cog6ToothIcon } from '@heroicons/react/24/solid';
import { FaPlus, FaSave, FaVial, FaRocket } from 'react-icons/fa';

import { useFlowContext } from '#/providers/FlowContext';
import { useUserContext } from '#/providers/UserContext';
import createActionNode from '#/services/workspaces/nodes/createActionNode';
import { IWorkspace } from '#/types/workspace';
import { stringifyJson } from '#/utils/json';

function HelpIcon() {
  return <HelpCircle className="h-5 w-5" />;
}

function SettingsIcon() {
  return <Cog6ToothIcon className="h-6 w-6" />;
}

function AddIcon() {
  return <FaPlus className="h-5 w-5" />;
}

function SaveIcon() {
  return <FaSave className="h-5 w-5" />;
}

function TestIcon() {
  return <FaVial className="h-5 w-5" />;
}

function DeployIcon() {
  return <FaRocket className="h-5 w-5" />;
}

function ActionButton(props: {
  title: string;
  Icon: React.ReactNode;
  isRunning?: boolean;
  onClick: () => void;
  tabIndex?: number;
}) {
  const { title, Icon, isRunning = false, onClick = noop, tabIndex } = props;

  return (
    <div>
      <label
        title={title}
        tabIndex={tabIndex}
        onClick={onClick}
        className={`pointer-events-auto btn border-0 btn-circle ${
          isRunning ? '' : 'hover:border-2 hover:border-secondary'
        }`}
      >
        {isRunning ? (
          <div className="h-[3rem] w-[3rem] relative">
            <div className="absolute h-full w-full flex flex-row justify-center items-center">
              <Icon className="h-5 w-5" />
            </div>

            {/* New div for spinning border */}
            <div className="absolute h-full w-full animate-spin rounded-full border-2 border-secondary border-r-transparent border-b-transparent motion-reduce:animate-[spin_1.5s_linear_infinite]"></div>
          </div>
        ) : (
          <Icon className="h-5 w-5" />
        )}
      </label>
    </div>
  );
}

const FlowInterfaces = (props: {
  workspaceUuid?: string;
  workspace: IWorkspace;
  isLateral?: boolean;
}) => {
  const { workspaceUuid, workspace, isLateral } = props;

  const { userSession, user } = useUserContext();
  const userAddress = userSession?.address;

  const { setCanDeleteNode, toggleIsSettingsShown, handleRunPreFlightCheck } =
    useFlowContext();

  useEffect(() => {
    console.debug('FlowInterfaces(', props, ')');
  }, []);

  const {
    setNodes,
    setEdges,
    addNodes,
    addEdges,
    deleteElements,
    getNodes,
    getEdges,
    project,
  } = useReactFlow();
  // const store = useStoreApi();
  // const [selectedElements, setSelectedElements] = useState<
  //   ReactFlowNode[],
  //   Edge[]
  // >;

  function handleGetHelp() {
    window.open(
      'https://simpfi-ai.gitbook.io/simpfi/overview/welcome-to-simpfi',
      '_blank',
    );
  }

  async function handleAddNode() {
    const uuid = uuidv4();
    const newNode = await createActionNode({
      id: uuid,
      position: {
        x: Math.random() * 400,
        y: Math.random() * 400,
      },
      data: {
        uuid,
        serviceKey: 'openai',
        actionKey: 'post /chat/completions',
      },
    });
    console.debug('[handleAddNode] newNode:', newNode);
    setNodes((nodes) => nodes.concat(newNode));
    // addNodes(newNode);
  }

  async function handleOpenSettings() {
    setCanDeleteNode(false);
    toggleIsSettingsShown();
  }

  // const onNodesDelete = useCallback(
  //   (deleted: ) => {
  //     setEdges(
  //       deleted.reduce((acc, node) => {
  //         const incomers = getIncomers(node, nodes, edges);
  //         const outgoers = getOutgoers(node, nodes, edges);
  //         const connectedEdges = getConnectedEdges([node], edges);

  //         const remainingEdges = acc.filter(
  //           (edge) => !connectedEdges.includes(edge),
  //         );

  //         const createdEdges = incomers.flatMap(({ id: source }) =>
  //           outgoers.map(({ id: target }) => ({
  //             id: `${source}->${target}`,
  //             source,
  //             target,
  //           })),
  //         );

  //         return [...remainingEdges, ...createdEdges];
  //       }, edges),
  //     );
  //   },
  //   [nodes, edges],
  // );

  // useOnSelectionChange({
  //   onChange: ({ nodes, edges }) => {
  //     return [setSelectedNodes(nodes), setSelectedEdges(edges) ];
  //   },
  // });

  const [isSaving, setIsSaving] = useState(false);
  async function saveWorkspace() {
    if (userAddress == null || user == null) {
      return false;
    }
    const updatedWorkspace = {
      nodes: getNodes(),
      edges: getConnectedEdges(getNodes(), getEdges()),
    };
    return await user.updateWorkspace({
      workspaceUuid: workspaceUuid ?? uuidv4(),
      updatedWorkspace,
    });
  }
  async function handleSave() {
    try {
      setCanDeleteNode(false);
      setIsSaving(true);
      const result = await saveWorkspace();
      console.debug('[handleSave] result:', result);
      toast.success('Save was successful!');
    } catch (error: any) {
      toast.error(`Save failed! Error: ${error?.message}`, {
        autoClose: false,
        closeOnClick: false,
        draggable: false,
      });
    } finally {
      setCanDeleteNode(true);
      setIsSaving(false);
    }
  }

  const [isTestRunning, setIsTestRunning] = useState(false);
  const triggerTestRun = async () => {
    try {
      if (workspaceUuid == null || workspaceUuid === '') {
        return;
      }
      const { isReadyToRun } = await handleRunPreFlightCheck();
      if (!isReadyToRun) {
        return;
      }
      console.info('[triggerTestRun]');
      const response = await fetch(
        `/api/_/workflows/${workspaceUuid}/test/run`,
        {
          method: 'POST',
        },
      );
      const responseStatus = response.status;
      console.debug('[triggerTestRun] responseStatus:', responseStatus);
      if (responseStatus >= 400) {
        const responseText = await response.text();
        throw new Error(responseText);
      }
      const responseJson = await response.json();
      console.debug('[triggerTestRun] responseJson:', responseJson);
      const result = responseJson?.result;
      const resultStatus = (result?.status ?? '').toUpperCase();
      if (['ERROR', 'FAILED'].includes(resultStatus)) {
        const error = result?.error;
        const errorMessage = error?.message ?? JSON.stringify(error);
        console.error(`[triggerTestRun] workflow run error: ${errorMessage}`);
        throw new Error(`Error running workflow: ${errorMessage}`);
      }
      return result;
    } catch (error: any) {
      console.error(error);
      throw new Error(`Failed test run: ${error}`);
    }
  };
  async function handleTestRun() {
    try {
      setCanDeleteNode(false);
      setIsTestRunning(true);
      const result = await triggerTestRun();
      if (result?.status == 'SUCCESS') {
        console.debug('[handleTestRun] result:', result);
        const resultValueString = stringifyJson(result?.value);
        console.debug('[handleTestRun] resultValueString:', resultValueString);
        toast.success(`Run was successful! Result: ${resultValueString}`);
      } else if (result?.status == 'ERROR') {
        toast.error(`Run failed! Error: ${result?.error}`, {
          autoClose: false,
          closeOnClick: false,
          draggable: false,
        });
      }
    } catch (error: any) {
      setCanDeleteNode(true);
      toast.error(`Run failed! Error: ${error?.message}`, {
        autoClose: false,
        closeOnClick: false,
        draggable: false,
      });
    } finally {
      setCanDeleteNode(true);
      setIsTestRunning(false);
    }
  }

  const [isDeploying, setIsDeploying] = useState(false);
  // const [wasDeploySuccess, setWasDeploySuccess] = useState(undefined);
  async function triggerDeployment() {
    try {
      if (workspaceUuid == null || workspaceUuid === '') {
        return;
      }
      const { isReadyToRun } = await handleRunPreFlightCheck();
      if (!isReadyToRun) {
        return;
      }
      console.info('[triggerDeployment]');
      const response = await fetch(`/api/_/workflows/${workspaceUuid}/deploy`, {
        method: 'POST',
      });
      console.debug('[triggerDeployment] response:', response);
      const responseStatus = response.status;
      console.debug('[triggerDeployment] responseStatus:', responseStatus);
      if (responseStatus >= 400) {
        const responseText = await response.text();
        throw new Error(responseText);
      }
      if (responseStatus == 200) {
        return {
          status: 'SUCCESS',
        };
      }
    } catch (error: any) {
      throw new Error(`Deployment failed: ${error}`);
    }
  }
  async function handleDeployment() {
    try {
      setCanDeleteNode(false);
      setIsDeploying(true);
      const result = await triggerDeployment();
      if (result?.status == 'SUCCESS') {
        toast.success('Deploy was successful!');
      }
    } catch (error: any) {
      setCanDeleteNode(true);
      toast.error(`Deploy failed! Error: ${error?.message}`, {
        autoClose: false,
        closeOnClick: false,
        draggable: false,
      });
    } finally {
      setCanDeleteNode(true);
      setIsDeploying(false);
    }
  }

  return (
    <>
      <Controls showInteractive={true} />

      <svg>
        <defs>
          <linearGradient id="edge-gradient">
            <stop offset="0%" stopColor="#ae53ba" />
            <stop offset="100%" stopColor="#2a8af6" />
          </linearGradient>

          <marker
            id="edge-circle"
            viewBox="-5 -5 10 10"
            refX="0"
            refY="0"
            markerUnits="strokeWidth"
            markerWidth="10"
            markerHeight="10"
            orient="auto"
          >
            <circle stroke="#2a8af6" strokeOpacity="0.75" r="2" cx="0" cy="0" />
          </marker>
        </defs>
      </svg>

      <MiniMap zoomable pannable />

      {/* ToolBar */}
      <div className="flex flex-col items-center justify-center w-full h-full">
        <div className="w-full h-full invisible md:visible fixed z-10 pointer-events-none">
          <div className="p-8 pt-10 absolute left-0 flex flex-col items-end gap-4">
            <ActionButton
              title="SimpFi Docs"
              Icon={HelpIcon}
              onClick={handleGetHelp}
              tabIndex={0}
            />
            <ActionButton
              title="Settings"
              Icon={SettingsIcon}
              onClick={handleOpenSettings}
              tabIndex={1}
            />
            <ActionButton
              title="Add Node"
              Icon={AddIcon}
              onClick={handleAddNode}
              tabIndex={2}
            />
            <ActionButton
              title="Save"
              Icon={SaveIcon}
              isRunning={isSaving}
              onClick={handleSave}
              tabIndex={3}
            />
            <ActionButton
              title="Test"
              Icon={TestIcon}
              isRunning={isTestRunning}
              onClick={handleTestRun}
              tabIndex={4}
            />
            <ActionButton
              title="Deploy"
              Icon={DeployIcon}
              isRunning={isDeploying}
              onClick={handleDeployment}
              tabIndex={5}
            />
          </div>
        </div>
      </div>
    </>
  );
};
export default FlowInterfaces;
